import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpResponse } from '@angular/common/http';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, finalize, of, take } from 'rxjs';
import { CaseManagementService } from '../../../../../../../../services/case-management.service';
import { CaseAttachmentModel } from '@models/case-attachment.model';
import { ConfirmDialogModel, ConfirmDialogService, PreloaderColor, UiPreloaderComponent } from '@ui';
import { UiSearchComponent } from '../../../../../../../ui-kit/ui-search/ui-search.component';

@Component({
  selector: 'app-case-manager-files-preview',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TranslatePipe,
    UiPreloaderComponent,
    UiSearchComponent,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
  templateUrl: './case-manager-files-preview.component.html',
  styleUrls: ['../case-manager-feature-preview.shared.scss'],
})
export class CaseManagerFilesPreviewComponent implements OnInit {
  private readonly caseManagementService = inject(CaseManagementService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirm = inject(ConfirmDialogService);

  readonly caseId = input.required<string>();
  readonly canDownload = input<boolean>(false);
  readonly canEdit = input<boolean>(false);
  readonly editAttachment = output<CaseAttachmentModel.CaseAttachmentDocument>();

  protected readonly preloaderColor = PreloaderColor.blue;
  protected readonly attachments = signal<CaseAttachmentModel.CaseAttachmentDocument[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly searchTerm = signal('');
  protected readonly downloadingFileId = signal<string | null>(null);
  protected readonly deletingFileId = signal<string | null>(null);
  protected readonly filteredAttachments = computed(() => {
    const query = this.searchTerm().trim().toLowerCase();
    if (!query) {
      return this.attachments();
    }

    return this.attachments().filter((attachment) => [
      attachment.filename,
      attachment.lastComment ?? '',
      attachment.createdByName,
      attachment.location?.address ?? '',
    ].some((value) => value.toLowerCase().includes(query)));
  });

  public ngOnInit(): void {
    this.loadAttachments();
  }

  /** Reload the attachment list, e.g. after a new file is added via the dialog. */
  public reload(): void {
    this.loadAttachments();
  }

  protected downloadAttachment(attachment: CaseAttachmentModel.CaseAttachmentDocument): void {
    if (!this.canDownload()) {
      return;
    }

    this.downloadingFileId.set(attachment._id);
    this.caseManagementService.downloadAttachment(attachment._id)
      .pipe(
        finalize(() => this.downloadingFileId.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => this.saveAttachment(response, attachment.filename));
  }

  /** Ask the parent to open the (route-free) edit/view dialog for this attachment. */
  protected requestEdit(attachment: CaseAttachmentModel.CaseAttachmentDocument): void {
    this.editAttachment.emit(attachment);
  }

  protected deleteAttachment(attachment: CaseAttachmentModel.CaseAttachmentDocument): void {
    if (!this.canEdit() || this.deletingFileId()) {
      return;
    }

    this.confirm
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'CASES.CREATE.DELETE_ATTACHMENT_TITLE',
        msg: 'CASES.CREATE.DELETE_ATTACHMENT_MESSAGE',
        confirm: 'CONFIRM_MODALS.COMMON_YES',
        cancel: 'COMMON.CANCEL',
        disableClose: true,
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.selection !== ConfirmDialogModel.ConfirmResult.OK) {
          return;
        }
        this.deletingFileId.set(attachment._id);
        this.caseManagementService
          .deleteAttachment(attachment._id, this.caseId())
          .pipe(
            finalize(() => this.deletingFileId.set(null)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe(() =>
            this.attachments.update((list) => list.filter((item) => item._id !== attachment._id)),
          );
      });
  }

  protected isBusy(attachment: CaseAttachmentModel.CaseAttachmentDocument): boolean {
    return this.downloadingFileId() === attachment._id || this.deletingFileId() === attachment._id;
  }

  private loadAttachments(): void {
    this.isLoading.set(true);
    this.caseManagementService.getAttachments(this.caseId())
      .pipe(
        catchError(() => of([])),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((attachments) => this.attachments.set(attachments));
  }

  private saveAttachment(response: HttpResponse<Blob>, fallbackFilename: string): void {
    if (!response.body) {
      return;
    }

    const filename = this.extractFilename(response.headers.get('content-disposition'), fallbackFilename);
    const url = window.URL.createObjectURL(response.body);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  private extractFilename(contentDisposition: string | null, fallbackFilename: string): string {
    if (!contentDisposition) {
      return fallbackFilename;
    }

    const utf8Match = /filename\*=UTF-8''([^;]+)/.exec(contentDisposition);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const plainMatch = /filename="?([^"]+)"?/.exec(contentDisposition);
    return plainMatch?.[1] ?? fallbackFilename;
  }
}
